import React, { useState } from 'react';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Toaster, toast } from 'sonner';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Target, Lightbulb, Users, Shield, TrendingUp, Network, CheckCircle, Building2, Zap, Briefcase, Menu, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || 'Message sent successfully!');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="App min-h-screen bg-white text-slate-900">
      <Toaster position="top-center" richColors />
      
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glassmorphic-nav border-b border-slate-200/50" data-testid="navbar">
        <div className="container mx-auto px-6 md:px-12 py-5">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3" data-testid="logo">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="8" fill="#3B82F6"/>
                <path d="M12 20L20 12L28 20L20 28L12 20Z" fill="white"/>
                <circle cx="20" cy="20" r="3" fill="#F87171"/>
              </svg>
              <span className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                <span className="text-blue-500">Nidus</span><span className="text-red-400">Ventures</span>
              </span>
            </a>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors" data-testid="nav-about">About</a>
              <a href="#platform" className="text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors" data-testid="nav-platform">Platform</a>
              <a href="#philosophy" className="text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors" data-testid="nav-philosophy">Philosophy</a>
              <a href="#careers" className="text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors" data-testid="nav-careers">Careers</a>
              <a href="#contact">
                <button className="bg-blue-500 text-white px-6 py-3 rounded-none font-medium hover:bg-blue-600 transition-colors" data-testid="nav-contact-btn">
                  Contact
                </button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-slate-900 p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4" data-testid="mobile-menu">
              <a 
                href="#about" 
                className="block text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-about"
              >
                About
              </a>
              <a 
                href="#platform" 
                className="block text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-platform"
              >
                Platform
              </a>
              <a 
                href="#philosophy" 
                className="block text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-philosophy"
              >
                Philosophy
              </a>
              <a 
                href="#careers" 
                className="block text-sm font-medium text-slate-600 hover:text-blue-500 transition-colors py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-careers"
              >
                Careers
              </a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <button className="w-full bg-blue-500 text-white px-6 py-3 rounded-none font-medium hover:bg-blue-600 transition-colors" data-testid="mobile-nav-contact-btn">
                  Contact
                </button>
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-12 bg-gradient-to-br from-blue-50 to-red-50" data-testid="hero-section">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div>
              <div className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-slate-500 mb-4" data-testid="hero-label">
                STRATEGIC HOLDING PLATFORM
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-tight font-bold text-slate-900 mb-6" data-testid="hero-headline">
                Building Excellence in Technology Services
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed mb-8" data-testid="hero-description">
                We consolidate ownership, make strategic investments, and enable collaboration across a curated ecosystem of technology consulting and services firms.
              </p>
              <a href="#platform">
                <button className="bg-blue-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-none font-medium hover:bg-blue-600 transition-colors inline-flex items-center gap-2 w-full md:w-auto justify-center" data-testid="hero-cta-btn">
                  Explore Platform <ArrowRight className="w-5 h-5" />
                </button>
              </a>
            </div>
            <div className="relative h-[300px] md:h-[500px] rounded-none overflow-hidden shadow-2xl" data-testid="hero-image">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/f1964864-02a3-4b66-9909-3d2a642fba02/images/df0b9c1ce141703f787083ea3ff2a72a7188c666dcf0281ff9750cb30c50ffa2.png"
                alt="Abstract 3D composition" 
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Role */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-white" data-testid="our-role-section">
        <div className="container mx-auto">
          <div className="max-w-4xl mb-12 md:mb-16">
            <div className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-4" data-testid="our-role-label">
              OUR ROLE
            </div>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl tracking-tight leading-snug font-medium text-slate-900 mb-6" data-testid="our-role-title">
              Investor and Strategic Operator
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600" data-testid="our-role-description">
              Nidus Ventures acts as both investor and operator—providing capital, strategic direction, and hands-on operational support. We don&apos;t just fund companies; we become long-term partners in building sustainable, scalable technology businesses that deliver exceptional value to enterprise clients.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-slate-50" data-testid="different-section">
        <div className="container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-12" data-testid="different-label">
            WHAT MAKES US DIFFERENT
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="professional-card p-10 rounded-none" data-testid="different-card-1">
              <Target className="w-12 h-12 text-blue-500 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">Long-term Alignment</h3>
              <p className="text-slate-600 leading-relaxed">We build for decades, not quarters. Our holding model ensures founder and investor incentives stay aligned through every stage of growth.</p>
            </div>
            <div className="professional-card p-10 rounded-none" data-testid="different-card-2">
              <Shield className="w-12 h-12 text-red-400 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">Operational Discipline</h3>
              <p className="text-slate-600 leading-relaxed">We bring systems, processes, and proven frameworks that transform growing firms into well-oiled operations ready for enterprise engagements.</p>
            </div>
            <div className="professional-card p-10 rounded-none" data-testid="different-card-3">
              <Network className="w-12 h-12 text-blue-500 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">Ecosystem Advantage</h3>
              <p className="text-slate-600 leading-relaxed">Portfolio companies collaborate, share resources, and cross-sell—creating a network effect that benefits everyone in the ecosystem.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-white" data-testid="focus-areas-section">
        <div className="container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-12" data-testid="focus-areas-label">
            FOCUS AREAS
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="professional-card p-10 rounded-none border-l-4 border-l-blue-500" data-testid="focus-card-1">
              <div className="text-red-400 font-bold text-sm mb-4">01</div>
              <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">Technology Consulting</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Strategic advisory, digital transformation, and technology strategy for enterprise clients.</p>
            </div>
            <div className="professional-card p-10 rounded-none border-l-4 border-l-blue-500" data-testid="focus-card-2">
              <div className="text-red-400 font-bold text-sm mb-4">02</div>
              <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">Software Development Services</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Custom software development, product engineering, and managed development teams.</p>
            </div>
            <div className="professional-card p-10 rounded-none border-l-4 border-l-blue-500" data-testid="focus-card-3">
              <div className="text-red-400 font-bold text-sm mb-4">03</div>
              <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">Specialized Tech Services</h3>
              <p className="text-slate-600 leading-relaxed text-sm">Cloud infrastructure, data engineering, AI/ML implementation, and other high-value technical services.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section id="about" className="py-20 md:py-32 lg:py-40 px-4 md:px-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white" data-testid="mission-vision-section">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-20">
            <div data-testid="mission-block">
              <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-200 mb-6">MISSION</div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium mb-8 tracking-tight leading-snug">
                Build a premier portfolio of technology services firms
              </h2>
              <p className="text-lg text-blue-100 leading-relaxed">
                We acquire ownership stakes and make strategic investments in established technology consulting and services companies. Our mission is to provide capital, operational support, and ecosystem access that enables sustainable growth and long-term value creation.
              </p>
            </div>
            <div data-testid="vision-block">
              <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-200 mb-6">VISION</div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium mb-8 tracking-tight leading-snug">
                The trusted platform for technology services excellence
              </h2>
              <p className="text-lg text-blue-100 leading-relaxed">
                We envision a future where Nidus Ventures is the first choice for founders seeking a partner who understands the unique challenges of services businesses—and for clients who demand enterprise-grade solutions backed by operational rigor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-slate-50" data-testid="core-principles-section">
        <div className="container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-12" data-testid="core-principles-label">
            CORE PRINCIPLES
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Founder-Friendly', desc: 'We respect founder vision and maintain operational independence while providing strategic guidance.' },
              { title: 'Client-First Mindset', desc: 'Every decision is filtered through one lens: does this improve outcomes for our portfolio companies\' clients?' },
              { title: 'Sustainable Growth', desc: 'We prioritize profitability and cash flow over vanity metrics. Growth must be profitable and repeatable.' },
              { title: 'Operational Excellence', desc: 'Systems, processes, and discipline turn good companies into great ones. We invest in infrastructure that scales.' },
              { title: 'Ecosystem Collaboration', desc: 'Portfolio companies are stronger together. We facilitate knowledge sharing, resource pooling, and joint opportunities.' },
              { title: 'Integrity & Transparency', desc: 'We build trust through honest communication, ethical business practices, and doing what we say we\'ll do.' }
            ].map((principle, idx) => (
              <div key={idx} className="professional-card p-8 rounded-none flex items-start gap-4" data-testid={`principle-card-${idx + 1}`}>
                <CheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading text-xl font-medium text-slate-900 mb-2">{principle.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{principle.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section id="platform" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-white relative" data-testid="platform-section">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/f1964864-02a3-4b66-9909-3d2a642fba02/images/479d4d8c9aef8c78decef46e18c67182dcf11c70738413a10f0004a555f50cba.png"
            alt="Ecosystem visualization" 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="relative z-10 container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-6" data-testid="platform-label">
            OUR PLATFORM
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium mb-8 tracking-tight max-w-3xl text-slate-900" data-testid="platform-title">
            Comprehensive infrastructure for scaling technology services
          </h2>
          <p className="text-xl text-slate-600 mb-16 max-w-4xl" data-testid="platform-description">
            Nidus Ventures provides capital, expertise, and infrastructure that help technology services firms scale efficiently.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="professional-card p-8 rounded-none" data-testid="platform-card-1">
              <Lightbulb className="w-10 h-10 text-blue-500 mb-4" />
              <h4 className="font-heading font-medium text-slate-900 mb-2">Strategic Capital</h4>
              <p className="text-sm text-slate-600">Patient, flexible financing for growth initiatives</p>
            </div>
            <div className="professional-card p-8 rounded-none" data-testid="platform-card-2">
              <Users className="w-10 h-10 text-red-400 mb-4" />
              <h4 className="font-heading font-medium text-slate-900 mb-2">Talent Network</h4>
              <p className="text-sm text-slate-600">Access to vetted professionals and leadership</p>
            </div>
            <div className="professional-card p-8 rounded-none" data-testid="platform-card-3">
              <TrendingUp className="w-10 h-10 text-blue-500 mb-4" />
              <h4 className="font-heading font-medium text-slate-900 mb-2">Business Development</h4>
              <p className="text-sm text-slate-600">Cross-selling and joint go-to-market strategies</p>
            </div>
            <div className="professional-card p-8 rounded-none" data-testid="platform-card-4">
              <Shield className="w-10 h-10 text-red-400 mb-4" />
              <h4 className="font-heading font-medium text-slate-900 mb-2">Shared Services</h4>
              <p className="text-sm text-slate-600">Finance, legal, HR, and operational infrastructure</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operating Support */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-slate-50" data-testid="operating-support-section">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-6" data-testid="operating-support-label">
                OPERATING SUPPORT
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium mb-6 tracking-tight text-slate-900" data-testid="operating-support-title">
                Hands-On Partnership
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-testid="operating-support-description">
                We don&apos;t just write checks. Our team rolls up their sleeves to help portfolio companies implement proven operating models, sales frameworks, and delivery methodologies.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4" data-testid="support-item-1">
                  <Zap className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Sales playbooks and pipeline management</span>
                </li>
                <li className="flex items-start gap-4" data-testid="support-item-2">
                  <Zap className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Financial planning and KPI tracking</span>
                </li>
                <li className="flex items-start gap-4" data-testid="support-item-3">
                  <Zap className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Recruiting and talent development</span>
                </li>
                <li className="flex items-start gap-4" data-testid="support-item-4">
                  <Zap className="w-5 h-5 text-blue-500 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Client delivery excellence frameworks</span>
                </li>
              </ul>
            </div>
            <div className="relative h-[300px] md:h-[500px] professional-card rounded-none overflow-hidden" data-testid="operating-support-image">
              <img 
                src="https://images.unsplash.com/photo-1651666176094-2bef8442db12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQ5ODExOTl8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern office architecture" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Partnerships */}
      <section className="py-24 px-6 md:px-12 bg-white border-t border-b border-slate-200" data-testid="ecosystem-section">
        <div className="container mx-auto mb-12">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-6" data-testid="ecosystem-label">
            ECOSYSTEM PARTNERSHIPS
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-medium mb-6 tracking-tight text-slate-900" data-testid="ecosystem-title">
            Collaborative Network
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl" data-testid="ecosystem-description">
            Our portfolio companies form a collaborative ecosystem where capabilities complement each other, enabling larger and more complex client engagements.
          </p>
        </div>
        <div className="bg-slate-50 py-8" data-testid="ecosystem-marquee">
          <Marquee gradient={false} speed={30}>
            <div className="mx-20 font-heading text-2xl tracking-wider text-slate-300">NIDUS TECHNOLOGIES</div>
            <div className="mx-20 font-heading text-2xl tracking-wider text-slate-300">STRATEGIC PARTNERS</div>
            <div className="mx-20 font-heading text-2xl tracking-wider text-slate-300">TECHNOLOGY CONSULTING</div>
            <div className="mx-20 font-heading text-2xl tracking-wider text-slate-300">ECOSYSTEM MEMBERS</div>
          </Marquee>
        </div>
      </section>

      {/* Portfolio Philosophy */}
      <section id="philosophy" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-slate-50" data-testid="philosophy-section">
        <div className="container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-12" data-testid="philosophy-label">
            PORTFOLIO PHILOSOPHY
          </div>
          <div className="grid lg:grid-cols-2 gap-12 text-lg text-slate-600 leading-relaxed" data-testid="philosophy-content">
            <div className="space-y-6">
              <p>
                We invest in technology services firms that have proven product-market fit, stable cash flows, and a clear path to sustainable growth. We&apos;re not looking for moonshots or hockey sticks—we want businesses built to last.
              </p>
              <p>
                Our ideal portfolio company has $2M-$20M in annual revenue, serves enterprise or mid-market clients, and is led by founders who value partnership over pure financial optimization. We prefer majority or significant minority stakes that give us meaningful governance rights.
              </p>
            </div>
            <div className="space-y-6">
              <p>
                Once invested, we take a patient, long-term approach. We&apos;re not flipping companies or optimizing for quick exits. We&apos;re building a platform that compounds value over decades through operational improvements, ecosystem synergies, and disciplined capital allocation.
              </p>
              <p>
                This isn&apos;t traditional private equity. We&apos;re not loading companies with debt, cutting costs to the bone, or manufacturing short-term gains. We&apos;re strategic partners who align incentives, share risks, and build alongside founders.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Nidus Ventures */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-white" data-testid="why-nidus-section">
        <div className="container mx-auto">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-12" data-testid="why-nidus-label">
            WHY NIDUS VENTURES
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="professional-card p-10 rounded-none" data-testid="why-card-1">
              <Briefcase className="w-12 h-12 text-blue-500 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">For Founders</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Find a partner who understands services businesses, respects your vision, and provides capital plus operational support to scale sustainably.
              </p>
              <div className="text-red-400 text-xs uppercase tracking-wider font-bold">Partnership-First</div>
            </div>
            <div className="professional-card p-10 rounded-none" data-testid="why-card-2">
              <Building2 className="w-12 h-12 text-red-400 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">For Clients</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Access a curated network of best-in-class technology services firms backed by operational rigor, financial stability, and enterprise-grade delivery.
              </p>
              <div className="text-red-400 text-xs uppercase tracking-wider font-bold">Excellence-Driven</div>
            </div>
            <div className="professional-card p-10 rounded-none" data-testid="why-card-3">
              <Users className="w-12 h-12 text-blue-500 mb-6" />
              <h3 className="font-heading text-2xl font-medium text-slate-900 mb-4">For Talent</h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Join a platform of growing technology firms with strong cultures, clear career paths, and exposure to complex, high-impact client work.
              </p>
              <div className="text-red-400 text-xs uppercase tracking-wider font-bold">Growth-Oriented</div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" className="py-16 md:py-24 lg:py-32 px-4 md:px-12 bg-gradient-to-br from-slate-50 to-blue-50" data-testid="careers-section">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-6" data-testid="careers-label">
            CAREERS
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold mb-8 tracking-tight text-slate-900" data-testid="careers-title">
            Always Open to Exceptional Talent
          </h2>
          <p className="text-xl text-slate-600 mb-10 leading-relaxed" data-testid="careers-description">
            We&apos;re always looking for exceptional talent to join our platform companies. If you&apos;re passionate about technology, excited by complex challenges, and want to work with a collaborative ecosystem of firms, we&apos;d love to hear from you.
          </p>
          <a href="#contact">
            <button className="bg-blue-500 text-white px-10 py-4 rounded-none font-medium hover:bg-blue-600 transition-colors" data-testid="careers-cta-btn">
              Get in Touch
            </button>
          </a>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 md:py-32 lg:py-40 px-4 md:px-12 bg-white" data-testid="contact-section">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-blue-500 mb-6" data-testid="contact-label">
              CONTACT
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6 tracking-tight text-slate-900" data-testid="contact-title">
              Let&apos;s Talk
            </h2>
            <p className="text-lg text-slate-600" data-testid="contact-description">
              Whether you&apos;re a founder exploring partnership opportunities or a client seeking exceptional technology services, we&apos;re here to help.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-none h-14"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-none h-14"
                  data-testid="contact-input-email"
                />
              </div>
            </div>
            <div>
              <Input
                type="text"
                name="company"
                placeholder="Company (Optional)"
                value={formData.company}
                onChange={handleChange}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-none h-14"
                data-testid="contact-input-company"
              />
            </div>
            <div>
              <Textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 rounded-none resize-none"
                data-testid="contact-input-message"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-500 text-white py-5 rounded-none font-medium text-base hover:bg-blue-600 transition-colors disabled:opacity-50"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 md:px-12 bg-slate-900 text-white" data-testid="footer">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="40" height="40" rx="8" fill="#3B82F6"/>
                  <path d="M12 20L20 12L28 20L20 28L12 20Z" fill="white"/>
                  <circle cx="20" cy="20" r="3" fill="#F87171"/>
                </svg>
                <div className="font-heading text-2xl md:text-3xl font-bold">
                  <span className="text-blue-400">Nidus</span><span className="text-red-400">Ventures</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm">
                Strategic holding for technology excellence
              </p>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Nidus Ventures. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
